import {
  CRASH_FORM_SECTION_KEYS,
  orderCrashFormSectionKeys,
} from './crash-form-sections';
import { msg } from '@lingui/macro';

/**
 * Structural snapshot for NaviTree — excludes free-text fields (e.g. Surname)
 * so the tree is not rebuilt on every keystroke.
 *
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, unknown>} defaults
 */
export function buildNavTreeStructure(formValues, defaults) {
  if (!formValues || !defaults) return {};

  const orderedKeys = orderCrashFormSectionKeys(
    Object.keys(formValues).filter((key) =>
      Object.prototype.hasOwnProperty.call(defaults, key),
    ),
    CRASH_FORM_SECTION_KEYS,
  );

  const result = {};

  for (const key of orderedKeys) {
    const value = formValues[key];

    if (key === 'crash' || key === 'road') {
      result[key] = {};
      continue;
    }

    if (key === 'units' && Array.isArray(value)) {
      result.units = value.map((unit, i) => ({
        trafficUnitId: unit['Traffic unit id'] ?? unit['Unit id'] ?? i + 1,
        type: unit.Type,
        people: (unit.people ?? []).map((person, j) => ({
          personId: person['Person id'] ?? j + 1,
        })),
      }));
      continue;
    }

    if (key === 'people' && Array.isArray(value)) {
      result.people = value.map((person, i) => ({
        personId: person['Person id'] ?? i + 1,
      }));
    }
  }

  return result;
}

/**
 * @param {ReturnType<typeof buildNavTreeStructure>} structure
 * @param {Record<string, unknown>} defaults
 * @param {(message: import('@lingui/macro').MessageDescriptor) => string} translate
 * @param {Array<{ value?: number, label?: string }>} unitsOptions
 */
export function composeTreeViewFromStructure(structure, defaults, translate, unitsOptions = []) {
  if (!structure || !defaults) return [];

  const orderedKeys = orderCrashFormSectionKeys(
    Object.keys(structure).filter((key) =>
      Object.prototype.hasOwnProperty.call(defaults, key),
    ),
    CRASH_FORM_SECTION_KEYS,
  );

  const treeView = orderedKeys.map((key) => {
    const value = structure[key];
    let result = { id: key, label: key, children: [] };

    if (key === 'crash') {
      result = { ...result, label: translate(msg`Crash data`) };
    }
    if (key === 'road') {
      result = { ...result, label: translate(msg`Road data`) };
    }

    if (key === 'units' && Array.isArray(value)) {
      return value.map((unit, i) => {
        const typeLabel = unit.type != null
          ? unitsOptions.find((option) => option.value === unit.type)?.label
          : '';
        return {
          id: `${key}.${i}`,
          label: `• ${translate(msg`Traffic unit`)} ${unit.trafficUnitId}${typeLabel ? ` - ${typeLabel}` : ''}`,
          children: (unit.people ?? []).map((person, j) => ({
            id: `${key}.${i}.people.${j}`,
            label: `◦ ${translate(msg`Person`)} ${person.personId}`,
          })),
        };
      });
    }

    if (key === 'people' && Array.isArray(value)) {
      return value.map((person, i) => ({
        id: `${key}.${i}`,
        label: `• ${translate(msg`Person`)} ${person.personId}`,
      }));
    }

    return result;
  });

  return treeView.filter((el) => el.id !== 'isNew' && el.id !== 'isModifying').flat();
}
